import React, { useMemo, useState } from "react";
import { CarFront, Contact, FileBadge2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KycStep } from "../components/KycStep";
import { StepFooter } from "../components/StepFooter";
import { StepHeader } from "../components/StepHeader";
import {
  DOCUMENT_TYPES,
  normalizeDocumentType,
} from "../services/documentRequirements";

const documentTypes = {
  passport: { value: DOCUMENT_TYPES.PASSPORT, icon: FileBadge2 },
  idCard: { value: DOCUMENT_TYPES.ID_CARD, icon: Contact },
  driverLicense: { value: DOCUMENT_TYPES.DRIVER_LICENSE, icon: CarFront },
};

const normalizeTypeKey = (value) => {
  const normalized = normalizeDocumentType(value);
  if (normalized === DOCUMENT_TYPES.PASSPORT) return "passport";
  if (normalized === DOCUMENT_TYPES.DRIVER_LICENSE) return "driverLicense";
  if (normalized === DOCUMENT_TYPES.ID_CARD) return "idCard";
  return null;
};

export function DocumentTypeStep({
  value,
  allowedTypes,
  onChange,
  onBack,
  onContinue,
}) {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const availableTypes = useMemo(() => {
    const normalized = Array.isArray(allowedTypes)
      ? allowedTypes.map(normalizeTypeKey).filter(Boolean)
      : [];
    return normalized.length
      ? [...new Set(normalized)]
      : Object.keys(documentTypes);
  }, [allowedTypes]);

  return (
    <KycStep
      labelledBy="kyc-document-type-title"
      className="document-type-step"
    >
      <StepHeader
        titleId="kyc-document-type-title"
        eyebrow={t("kyc.flow.steps.documentType")}
        title={t("kyc.document.title")}
        description={t("kyc.document.description")}
        compact
      />

      <div className="document-type-options grid gap-3 md:grid-cols-3">
        {availableTypes.map((key) => {
          const item = documentTypes[key];
          const Icon = item.icon;
          const selected = value === item.value;

          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                onChange(item.value);
                setError("");
              }}
              aria-pressed={selected}
              data-selected={selected}
              className={`document-type-option flex min-h-20 items-center gap-4 rounded-2xl border p-4 text-left transition ${
                selected
                  ? "border-teal-700 bg-stone-50 ring-1 ring-stone-900"
                  : "border-stone-200 bg-white hover:border-stone-400"
              }`}
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-stone-700">
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <span className="text-xs font-semibold text-stone-900">
                {t(`kyc.document.${key}`)}
              </span>
              <span
                className={`ml-auto size-3 rounded-full border-2 md:hidden ${
                  selected
                    ? "border-teal-700 bg-teal-700 ring-2 ring-white"
                    : "border-stone-300"
                }`}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
      {error && (
        <p className="mt-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <StepFooter
        onBack={onBack}
        onContinue={() => {
          if (!value) {
            setError(t("kyc.validation.documentType"));
            return;
          }
          onContinue();
        }}
      />
    </KycStep>
  );
}
